/**
 * R232q5i: Sirus-only nature props loader.
 *
 * После refactor мы НЕ грузим Quaternius CC0 OBJ pack — только Sirus
 * extracted .obj/.gltf/.glb из /public/models/wow-vanilla/nature/.
 * Если callsite запрашивает имя файла которого нет на disk — silent fail
 * (биом просто реже усеян) вместо throw.
 *
 * Multi-submesh trees: каждый file содержит trunk+canopy submeshes
 * с разными материалами из .mtl. Кешируем весь mesh group и клонируем
 * его целиком при каждом scatter.
 */

import {
  type AbstractMesh,
  Color3,
  type InstancedMesh,
  Material,
  type Mesh,
  type Scene,
  SceneLoader,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/OBJ';
import '@babylonjs/loaders/glTF';

/**
 * R232q5al: Sirus OBJ leaf/canopy PNG textures have an alpha channel that
 * the .mtl file does not declare. After SceneLoader import, leaf cards
 * render as opaque rectangles → on screen they look like red/black/green
 * "checkerboard" shapes scattered through the world (each card is a flat
 * billboard, the alpha would have masked it to a leaf silhouette).
 *
 * R232q5av: previous version applied ALPHATEST + double-sided to EVERY .png
 * material indiscriminately. That broke trunk textures (they're opaque
 * cylinders) — alpha-test sampling smudged them, double-sided rendering
 * doubled trunk geometry into a smeared mess. Trunks should remain solid
 * opaque. Now we narrow the alpha-test path to materials whose diffuse
 * texture name suggests foliage (leaf/canopy/bush/grass/branch/vine/
 * flower/shrub/needles/fern/moss/web/seaweed/lillypad/cattail/reed/
 * mushroom/plant/puff/blossom). Trunk/bark/rock textures get the
 * standard opaque path with backFaceCulling preserved.
 *
 * We use ALPHATEST (not ALPHABLEND) because leaves are dense and proper
 * sorting would tank perf at 1k+ instances.
 */
const FOLIAGE_KEYWORDS = [
  'leaf', 'leaves', 'canopy', 'bush', 'grass', 'branch', 'vine', 'flower',
  'shrub', 'needle', 'fern', 'moss', 'web', 'seaweed', 'lillypad', 'lilypad',
  'cattail', 'catail', 'reed', 'mushroom', 'plant', 'puff', 'blossom',
  'kelp', 'palm_leaves', 'fatleaf', 'eyes', 'pumpkin',
];

function isFoliageTexture(url: string): boolean {
  const lower = url.toLowerCase();
  return FOLIAGE_KEYWORDS.some((k) => lower.includes(k));
}

function fixupAlphaMaterials(meshes: Mesh[]): void {
  const seen = new Set<Material>();
  for (const mesh of meshes) {
    const mat = mesh.material;
    if (!mat || seen.has(mat)) continue;
    seen.add(mat);
    if (!(mat instanceof StandardMaterial)) continue;
    const diff = mat.diffuseTexture as { name?: string; url?: string; hasAlpha?: boolean; anisotropicFilteringLevel?: number } | null;
    if (!diff) continue;
    const url = (diff.name || diff.url || '').toLowerCase();
    if (!url.endsWith('.png')) continue;
    // R232q5av: anisotropic filtering for ALL nature textures — without
    // this the mip chain on tree trunks looks smudged at glancing angles
    // (player reported "стянутые/размазанные деревья"). 4× is the sweet
    // spot for browser GPUs (16× sometimes hits driver-cap warnings).
    if (typeof diff.anisotropicFilteringLevel === 'number') diff.anisotropicFilteringLevel = 4;
    if (isFoliageTexture(url)) {
      // Foliage: alpha-tested billboard, double-sided so leaves visible from below.
      diff.hasAlpha = true;
      mat.useAlphaFromDiffuseTexture = true;
      mat.transparencyMode = Material.MATERIAL_ALPHATEST;
      mat.backFaceCulling = false;
      // Slight emissive so leaves don't go pitch black under angled sun.
      if (mat.emissiveColor.r === 0 && mat.emissiveColor.g === 0 && mat.emissiveColor.b === 0) {
        mat.emissiveColor = new Color3(0.15, 0.15, 0.15);
      }
    } else {
      // Trunks / rocks / dirt: opaque, single-sided (no double-render
      // smearing). Explicitly mark hasAlpha=false so Babylon's auto-detect
      // doesn't mis-classify the texture as transparent.
      diff.hasAlpha = false;
      mat.useAlphaFromDiffuseTexture = false;
      mat.transparencyMode = Material.MATERIAL_OPAQUE;
      mat.backFaceCulling = true;
      // Small emissive so trunks don't go pitch black on shaded slopes.
      if (mat.emissiveColor.r === 0 && mat.emissiveColor.g === 0 && mat.emissiveColor.b === 0) {
        mat.emissiveColor = new Color3(0.10, 0.10, 0.10);
      }
    }
  }
}

/** Единственный путь — Sirus extracted nature props. */
const BASE_WOW = `${import.meta.env.BASE_URL}models/wow-vanilla/nature/`;

interface PropMaster {
  meshes: Mesh[];
}

const cache = new Map<string, Promise<PropMaster | null>>();

async function loadOBJ(scene: Scene, fileName: string): Promise<PropMaster | null> {
  if (cache.has(fileName)) return cache.get(fileName)!;
  const promise = (async (): Promise<PropMaster | null> => {
    try {
      const result = await SceneLoader.ImportMeshAsync('', BASE_WOW, fileName, scene);
      const meshes = result.meshes
        .filter((m) => (m as Mesh).getTotalVertices && (m as Mesh).getTotalVertices() > 0)
        .map((m) => m as Mesh);
      if (meshes.length === 0) return null;
      // R232q5al: fix opaque leaf-card rendering (the "checkerboard" the
      // user reported) BEFORE we hide the masters — alpha test must be on
      // before any instance is created.
      fixupAlphaMaterials(meshes);
      for (const m of meshes) {
        m.setEnabled(false);
        m.isPickable = false;
        // R232q86zcu: minimize per-frame cost on disabled masters.
        // doNotSyncBoundingInfo skips bbox recompute (master never moves).
        // freezeWorldMatrix locks transform calculation.
        m.doNotSyncBoundingInfo = true;
        m.freezeWorldMatrix();
        // R232q5ae: receiveShadows MUST live on the master Mesh — Babylon
        // logs a warning for every InstancedMesh that tries to set it
        // (22k+ console-spam observed in prod). Setting it here once
        // propagates to all instances created from this master.
        m.receiveShadows = true;
      }
      return { meshes };
    } catch {
      // Silent fail — биом просто будет реже усеян.
      return null;
    }
  })();
  cache.set(fileName, promise);
  return promise;
}

/** Spawn a single prop. Returns the parent TransformNode containing all
 *  submesh instances, or null on load failure. */
export async function spawnProp(
  scene: Scene,
  fileName: string,
  position: Vector3,
  scale = 1,
  rotationY = 0,
): Promise<AbstractMesh | TransformNode | null> {
  const master = await loadOBJ(scene, fileName);
  if (!master || master.meshes.length === 0) return null;
  const root = new TransformNode(`prop-${fileName}-${Math.random().toString(36).slice(2, 8)}`, scene);
  root.position = position;
  root.scaling = new Vector3(scale, scale, scale);
  root.rotation.y = rotationY;
  for (const m of master.meshes) {
    const inst: InstancedMesh = m.createInstance(`${m.name}-i-${Math.random().toString(36).slice(2, 6)}`);
    inst.parent = root;
    inst.isPickable = false;
    inst.position = m.position.clone();
    inst.rotation = m.rotation.clone();
    inst.scaling = m.scaling.clone();
    // R232q5ae: НЕ ставим receiveShadows на InstancedMesh — Babylon
    // выдаёт warning на каждый instance (22k+ spam в prod console).
    // Master mesh уже имеет receiveShadows=true в loadOBJ().
    // R84: props не двигаются — freeze world matrix.
    // R232q86zbg: trees, rocks, large bushes block the player.  PlayerCollision
    // skips meshes with width < 0.4 m (grass blades, flowers) at runtime, so
    // we can flip the flag here unconditionally — the runtime filter handles
    // the small props.
    (inst as { checkCollisions?: boolean }).checkCollisions = true;
    inst.freezeWorldMatrix();
  }
  root.computeWorldMatrix(true);
  return root;
}

/** Scatter many props of given types across an XZ rectangle (rangeXZ) or disk (diskRadius). */
export async function scatterProps(scene: Scene, opts: {
  fileNames: string[];
  count: number;
  rangeXZ?: number;
  skipRadius?: number;
  scaleMin?: number;
  scaleMax?: number;
  rng: () => number;
  yJitter?: number;
  /** Optional centre {x, z} for biome-localised scatter. */
  centerX?: number;
  centerZ?: number;
  /** When centered, sample with disk-radius instead of square. */
  diskRadius?: number;
}): Promise<void> {
  const skip = opts.skipRadius ?? 8;
  const sMin = opts.scaleMin ?? 1;
  const sMax = opts.scaleMax ?? 1;
  const yJ = opts.yJitter ?? 0;
  const cx = opts.centerX ?? 0;
  const cz = opts.centerZ ?? 0;
  // R232q86e: HARD-skip far scatters + cap count by distance.
  // Previous version delayed via setTimeout — far biomes still loaded after
  // 30s and pushed RAM past Chrome's 4 GB tab limit → "Опаньки" OOM crash
  // юзер reported. Now: > 1500m = full skip (player will never see those at
  // current draw-distance with fog). 800-1500m = quarter density.
  try {
    const origin = (typeof window !== 'undefined') ? (window as unknown as { __aiwariLazyOrigin?: { x: number; z: number } }).__aiwariLazyOrigin : undefined;
    if (origin) {
      const d = Math.hypot(cx - origin.x, cz - origin.z);
      // R232q86l2: bumped threshold so biomes at canonical centres (±1500m)
      // still spawn (just at quarter density). Player at (0,0) was getting
      // 133 instances of 5000 budget because all biomes >1200m → skipped.
      if (d > 2500) return;                 // Hard skip — really far.
      if (d > 1000) opts = { ...opts, count: Math.floor(opts.count / 4) };
      else if (d > 400) opts = { ...opts, count: Math.floor(opts.count / 2) };
    }
  } catch { /* ignore */ }
  // R232q86l: global instance budget bumped 2000 → 5000. Combined with
  // shared-master NPC pipeline (1 GLB cache instead of 6+) we have ~150 MB
  // headroom; 5k Babylon InstancedMesh nodes = ~50 MB extra. Still under
  // Chrome's safe tab budget.
  const counter = (typeof window !== 'undefined')
    ? (window as unknown as { __aiwariScatterUsed?: { v: number } })
    : undefined;
  if (counter) {
    counter.__aiwariScatterUsed = counter.__aiwariScatterUsed ?? { v: 0 };
    const used = counter.__aiwariScatterUsed.v;
    const SCATTER_BUDGET = 12000;
    if (used >= SCATTER_BUDGET) return;
    if (used + opts.count > SCATTER_BUDGET) {
      opts = { ...opts, count: Math.max(0, SCATTER_BUDGET - used) };
    }
    counter.__aiwariScatterUsed.v = used + opts.count;
  }
  // Если массив пустой — silent skip (call site сам понимает что биом без props).
  if (opts.fileNames.length === 0) return;
  await Promise.all(opts.fileNames.map((f) => loadOBJ(scene, f)));
  const promises: Promise<unknown>[] = [];
  for (let i = 0; i < opts.count; i++) {
    const r = opts.rng;
    let x: number, z: number;
    if (opts.diskRadius != null) {
      const a = r() * Math.PI * 2;
      const radius = Math.sqrt(r()) * opts.diskRadius;
      x = cx + Math.cos(a) * radius;
      z = cz + Math.sin(a) * radius;
    } else {
      const range = opts.rangeXZ ?? 100;
      x = cx + (r() - 0.5) * 2 * range;
      z = cz + (r() - 0.5) * 2 * range;
    }
    if (Math.hypot(x - cx, z - cz) < skip && opts.diskRadius == null) continue;
    if (opts.diskRadius != null && Math.hypot(x - cx, z - cz) < skip) continue;
    const file = opts.fileNames[Math.floor(r() * opts.fileNames.length)]!;
    const scale = sMin + r() * (sMax - sMin);
    const yaw = r() * Math.PI * 2;
    const y = yJ ? (r() - 0.5) * yJ : 0;
    promises.push(spawnProp(scene, file, new Vector3(x, y, z), scale, yaw));
  }
  await Promise.all(promises);
}
